import type { ChangeEvent, JSX } from 'react';

interface RangeNumberInputGroupProps {
  title: string;
  description: string;
  minLabel: string;
  maxLabel: string;
  placeholder: string;
  suffix?: string;
  minValue: number | null;
  maxValue: number | null;
  disabled: boolean;
  onMinChange: (value: number | null) => void;
  onMaxChange: (value: number | null) => void;
  error?: string | null;
  errorId?: string;
}

export function RangeNumberInputGroup({
  title,
  description,
  minLabel,
  maxLabel,
  placeholder,
  suffix,
  minValue,
  maxValue,
  disabled,
  onMinChange,
  onMaxChange,
  error,
  errorId,
}: RangeNumberInputGroupProps): JSX.Element {
  const hasError = Boolean(error);
  const toNumberInputValue = (candidate: number | null) =>
    typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : '';
  const handleInputChange =
    (handler: (value: number | null) => void) => (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      const trimmed = rawValue.trim();
      if (trimmed.length === 0) {
        handler(null);
        return;
      }
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed)) {
        return;
      }
      handler(parsed);
    };

  return (
    <div className="rounded-2xl border border-surface-subtle bg-surface-subtle/40 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">{title}</p>
      <p className="mt-1 text-sm text-text-primary/70">{description}</p>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          {minLabel}
          <div className="flex items-center gap-2 rounded-2xl border border-surface-subtle bg-white px-3 py-2.5 md:py-2">
            <input
              type="text"
              inputMode="decimal"
              value={toNumberInputValue(minValue)}
              onChange={handleInputChange(onMinChange)}
              placeholder={placeholder}
              aria-invalid={hasError ? true : undefined}
              aria-describedby={hasError ? errorId : undefined}
              disabled={disabled}
              className="w-full border-none bg-transparent text-base font-semibold text-brand-primary outline-none md:text-sm"
            />
            {typeof suffix === 'string' && suffix.length > 0 ? (
              <span className="text-text-primary/70">{suffix}</span>
            ) : null}
          </div>
        </label>
        <label className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          {maxLabel}
          <div className="flex items-center gap-2 rounded-2xl border border-surface-subtle bg-white px-3 py-2.5 md:py-2">
            <input
              type="text"
              inputMode="decimal"
              value={toNumberInputValue(maxValue)}
              onChange={handleInputChange(onMaxChange)}
              placeholder={placeholder}
              aria-invalid={hasError ? true : undefined}
              aria-describedby={hasError ? errorId : undefined}
              disabled={disabled}
              className="w-full border-none bg-transparent text-base font-semibold text-brand-primary outline-none md:text-sm"
            />
            {typeof suffix === 'string' && suffix.length > 0 ? (
              <span className="text-text-primary/70">{suffix}</span>
            ) : null}
          </div>
        </label>
      </div>
      {hasError ? (
        <p id={errorId} className="mt-2 text-xs font-semibold text-rose-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
